import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import * as React from 'react';
import * as RpnInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import es from 'react-phone-number-input/locale/es.json';

import { cn } from '../../utils/cn';
import { Button } from '../button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../command';
import { Input } from '../input';
import { LoadingAddon } from '../loading-addon';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { ScrollArea } from '../scroll-area';

type InputPhoneProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref'> &
  Omit<RpnInput.Props<typeof RpnInput.default>, 'onChange'> & {
    isLoading?: boolean;
    onChange?: (value: RpnInput.Value) => void;
  };

const InputPhone: React.ForwardRefExoticComponent<InputPhoneProps> = React.forwardRef<
  React.ElementRef<typeof RpnInput.default>,
  InputPhoneProps
>(({ className, onChange, value, defaultCountry = 'MX', isLoading = false, ...props }, ref) => {
  return (
    // eslint-disable-next-line react/jsx-pascal-case
    <div className="relative">
      <RpnInput.default
        autoComplete="off"
        className={cn('flex', className)}
        countrySelectComponent={CountrySelect}
        defaultCountry={defaultCountry}
        flagComponent={FlagComponent}
        inputComponent={InputComponent}
        labels={es}
        ref={ref}
        smartCaret={false}
        value={typeof value === 'string' ? value : undefined}
        /**
         * Handles the onChange event.
         *
         * react-phone-number-input might trigger the onChange event as undefined
         * when a valid phone number is not entered. To prevent this,
         * the value is coerced to an empty string.
         *
         * @param {E164Number | undefined} value - The entered value
         */
        onChange={(value) => onChange?.(value || ('' as RpnInput.Value))}
        {...props}
      />
      {isLoading && <LoadingAddon type="plain" />}
    </div>
  );
});
InputPhone.displayName = 'InputPhone';

const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => (
    <Input className={cn('rounded-s-none rounded-e-lg', className)} {...props} ref={ref} />
  ),
);
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RpnInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RpnInput.Country;
  options: CountryEntry[];
  onChange: (country: RpnInput.Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={isOpen}
          type="button"
          variant="outline"
          className="rounded-r-none"
          disabled={disabled}
          role="combobox"
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          <ChevronDownIcon
            className={cn('-mr-2 size-4 opacity-50', disabled ? 'hidden' : 'opacity-100')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-(--radix-popover-content-available-height) p-0"
      >
        <Command defaultValue={selectedCountry}>
          <CommandInput placeholder="Buscar país" />
          <CommandList className="h-40">
            <ScrollArea className="h-40">
              <CommandEmpty>Sin resultados 🫥</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CommandItem
                      key={value}
                      keywords={[label, value]}
                      value={value}
                      onSelect={() => {
                        onChange(value);
                        setIsOpen(false);
                      }}
                    >
                      <CountrySelectOption
                        country={value}
                        countryName={label}
                        selectedCountry={selectedCountry}
                      />
                    </CommandItem>
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps extends RpnInput.FlagProps {
  selectedCountry: RpnInput.Country;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
}: CountrySelectOptionProps) => {
  return (
    <>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-base">{countryName}</span>
      <span className="text-sm text-muted-foreground">{`+${RpnInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </>
  );
};

const FlagComponent = ({ country, countryName }: RpnInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-lg [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { InputPhone };
